import React, { useState, useEffect } from "react";
import { X, Search, Calendar, Save, Building2, Check } from "lucide-react";
import axios from "axios";
import { getApiUrl } from "../../config/api";

interface School {
    id: number;
    name: string;
    subdomain: string;
    billing_email: string;
    status: string;
}

interface AddSubscriptionModalProps {
    isOpen: boolean;
    onClose: () => void;
    onSuccess: () => void;
}

const AddSubscriptionModal: React.FC<AddSubscriptionModalProps> = ({ isOpen, onClose, onSuccess }) => {
    const [step, setStep] = useState(1); // 1: Search School, 2: Subscription Data
    const [schoolSearch, setSchoolSearch] = useState("");
    const [schools, setSchools] = useState<School[]>([]);
    const [selectedSchool, setSelectedSchool] = useState<School | null>(null);
    const [searching, setSearching] = useState(false);

    const [formData, setFormData] = useState({
        plan_type: "monthly",
        amount: "",
        currency: "ZMW",
        status: "active",
        current_period_start: new Date().toISOString().split('T')[0],
        current_period_end: ""
    });

    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string>("");
    const token = localStorage.getItem("token") || "";

    // Calculate end date based on plan type
    useEffect(() => {
        if (formData.current_period_start && formData.plan_type) {
            const start = new Date(formData.current_period_start);
            const end = new Date(start);

            if (formData.plan_type === "monthly") end.setMonth(end.getMonth() + 1);
            else if (formData.plan_type === "quarterly") end.setMonth(end.getMonth() + 3);
            else if (formData.plan_type === "annual") end.setFullYear(end.getFullYear() + 1);

            setFormData(prev => ({ ...prev, current_period_end: end.toISOString().split('T')[0] }));
        }
    }, [formData.current_period_start, formData.plan_type]);

    // Search schools
    useEffect(() => {
        if (schoolSearch.length > 2) {
            const delayDebounceFn = setTimeout(() => {
                handleSearch();
            }, 500);
            return () => clearTimeout(delayDebounceFn);
        } else {
            setSchools([]);
        }
    }, [schoolSearch]);

    const handleSearch = async () => {
        try {
            setSearching(true);
            const response = await axios.get(
                getApiUrl(`superadmin/schools?search=${schoolSearch}`),
                { headers: { Authorization: `Bearer ${token}` } }
            );
            setSchools(response.data || []);
        } catch (err) {
            console.error("Error searching schools:", err);
        } finally {
            setSearching(false);
        }
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        if (!selectedSchool) return;

        setLoading(true);
        setError("");

        try {
            await axios.post(
                getApiUrl(`superadmin/schools/${selectedSchool.id}/subscriptions`),
                {
                    ...formData,
                    amount: parseFloat(formData.amount)
                },
                { headers: { Authorization: `Bearer ${token}` } }
            );
            onSuccess();
            handleClose();
        } catch (err: any) {
            setError(err.response?.data?.message || "Failed to create subscription");
        } finally {
            setLoading(false);
        }
    };

    const handleClose = () => {
        setStep(1);
        setSchoolSearch("");
        setSelectedSchool(null);
        setFormData({
            plan_type: "monthly",
            amount: "",
            currency: "ZMW",
            status: "active",
            current_period_start: new Date().toISOString().split('T')[0],
            current_period_end: ""
        });
        setError("");
        onClose();
    };

    if (!isOpen) return null;

    return (
        <div className="fixed inset-0 z-50 overflow-y-auto">
            <div className="flex items-center justify-center min-h-screen px-4 pt-4 pb-20 text-center sm:block sm:p-0">
                <div className="fixed inset-0 transition-opacity" aria-hidden="true" onClick={handleClose}>
                    <div className="absolute inset-0 bg-gray-500 opacity-75"></div>
                </div>

                <span className="hidden sm:inline-block sm:align-middle sm:h-screen" aria-hidden="true">&#8203;</span>

                <div className="inline-block align-bottom bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full">
                    <div className="bg-white px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
                        <div className="flex justify-between items-center mb-4">
                            <h3 className="text-lg font-medium text-gray-900">
                                {step === 1 ? "Step 1: Select School" : "Step 2: Subscription Details"}
                            </h3>
                            <button onClick={handleClose} className="text-gray-400 hover:text-gray-500">
                                <X className="h-6 w-6" />
                            </button>
                        </div>

                        {error && (
                            <div className="mb-4 bg-red-50 border border-red-200 text-red-700 px-4 py-2 rounded text-sm">
                                {error}
                            </div>
                        )}

                        {step === 1 ? (
                            <div className="space-y-4">
                                <div className="relative">
                                    <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
                                    <input
                                        type="text"
                                        placeholder="Search school name or subdomain..."
                                        value={schoolSearch}
                                        onChange={(e) => setSchoolSearch(e.target.value)}
                                        className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500"
                                        autoFocus
                                    />
                                </div>

                                <div className="max-h-60 overflow-y-auto space-y-2">
                                    {searching ? (
                                        <div className="text-center py-4 text-gray-500 text-sm">Searching...</div>
                                    ) : schools.length > 0 ? (
                                        schools.map(school => (
                                            <button
                                                key={school.id}
                                                onClick={() => setSelectedSchool(school)}
                                                className={`w-full flex items-center justify-between p-3 rounded-lg border text-left transition-colors ${selectedSchool?.id === school.id
                                                        ? 'border-teal-500 bg-teal-50'
                                                        : 'border-gray-200 hover:bg-gray-50'
                                                    }`}
                                            >
                                                <div className="flex items-center">
                                                    <Building2 className={`h-5 w-5 mr-3 ${selectedSchool?.id === school.id ? 'text-teal-600' : 'text-gray-400'}`} />
                                                    <div>
                                                        <div className="font-medium text-gray-900">{school.name}</div>
                                                        <div className="text-xs text-gray-500">{school.subdomain}.milestone.com</div>
                                                    </div>
                                                </div>
                                                {selectedSchool?.id === school.id && <Check className="h-5 w-5 text-teal-600" />}
                                            </button>
                                        ))
                                    ) : schoolSearch.length > 2 ? (
                                        <div className="text-center py-4 text-gray-500 text-sm">No schools found</div>
                                    ) : (
                                        <div className="text-center py-4 text-gray-400 text-sm italic">Type at least 3 characters to search</div>
                                    )}
                                </div>

                                <div className="mt-5 flex justify-end">
                                    <button
                                        disabled={!selectedSchool}
                                        onClick={() => setStep(2)}
                                        className="px-4 py-2 bg-teal-600 text-white rounded-lg hover:bg-teal-700 disabled:opacity-50"
                                    >
                                        Next Step
                                    </button>
                                </div>
                            </div>
                        ) : (
                            <form onSubmit={handleSubmit} className="space-y-4">
                                <div className="flex items-center p-3 bg-gray-50 rounded-lg mb-4">
                                    <Building2 className="h-5 w-5 text-teal-600 mr-2" />
                                    <div>
                                        <div className="text-sm font-bold text-gray-900">{selectedSchool?.name}</div>
                                        <div className="text-xs text-gray-500">Activating subscription for this school</div>
                                    </div>
                                </div>

                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">Plan Type</label>
                                    <select
                                        value={formData.plan_type}
                                        onChange={(e) => setFormData({ ...formData, plan_type: e.target.value })}
                                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-teal-500"
                                        required
                                    >
                                        <option value="monthly">Monthly</option>
                                        <option value="quarterly">Quarterly</option>
                                        <option value="annual">Annual</option>
                                    </select>
                                </div>

                                <div className="grid grid-cols-2 gap-4">
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-1">Currency</label>
                                        <select
                                            value={formData.currency}
                                            onChange={(e) => setFormData({ ...formData, currency: e.target.value })}
                                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-teal-500"
                                        >
                                            <option value="ZMW">ZMW</option>
                                            <option value="USD">USD</option>
                                        </select>
                                    </div>
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-1">Amount</label>
                                        <input
                                            type="number"
                                            value={formData.amount}
                                            onChange={(e) => setFormData({ ...formData, amount: e.target.value })}
                                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-teal-500"
                                            placeholder="0.00"
                                            required
                                        />
                                    </div>
                                </div>

                                <div className="grid grid-cols-1 gap-4">
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-1">Start Date</label>
                                        <div className="relative">
                                            <Calendar className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                                            <input
                                                type="date"
                                                value={formData.current_period_start}
                                                onChange={(e) => setFormData({ ...formData, current_period_start: e.target.value })}
                                                className="w-full pl-9 pr-3 py-2 border border-gray-300 rounded-lg focus:ring-teal-500"
                                                required
                                            />
                                        </div>
                                    </div>
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-1">End Date (Auto)</label>
                                        <div className="relative">
                                            <Calendar className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                                            <input
                                                type="date"
                                                value={formData.current_period_end}
                                                className="w-full pl-9 pr-3 py-2 border border-gray-300 rounded-lg bg-gray-50 text-gray-500"
                                                readOnly
                                            />
                                        </div>
                                    </div>
                                </div>

                                <div className="mt-6 flex justify-between">
                                    <button
                                        type="button"
                                        onClick={() => setStep(1)}
                                        className="px-4 py-2 text-gray-600 hover:text-gray-900"
                                    >
                                        Back
                                    </button>
                                    <button
                                        type="submit"
                                        disabled={loading}
                                        className="flex items-center px-6 py-2 bg-teal-600 text-white rounded-lg hover:bg-teal-700 disabled:opacity-50"
                                    >
                                        <Save className="h-5 w-5 mr-2" />
                                        {loading ? "Activating..." : "Submit & Activate"}
                                    </button>
                                </div>
                            </form>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
};

export default AddSubscriptionModal;
